import React, { useEffect, useState } from 'react';
import { Chart } from 'chart.js';
import { LoadingPage } from 'ensightful-koala-ui';
import moment from 'moment';
import { styled } from '@mui/material';

import { useAuth } from 'contexts/auth';
import { useUser2 } from 'contexts/user2';
import {
    Box,
    Checkbox,
    Container,
    Drawer,
    IconButton,
    Radio,
    RadioGroup,
    Stack,
    Text,
    Tooltip,
} from 'shell/components';
import theme from 'shell/themes';
import { getUserPreferredFullName } from 'utils/helper';

const Mobile = ({
    state,
    setState,
    handleChange,
    currentDateRange,
    setCurrentDateRange,
    activitySets,
    getGraphTitle,
    getGraphSubtitle,
    getDateRange,
    renderExternalTooltip,
    getLegendItem,
}) => {
    const { auth } = useAuth();
    const { currentGroupAccountUsers } = useUser2();

    const [open, setOpen] = useState(false);
    const [tickGap, setTickGap] = useState(1);

    useEffect(() => {
        const activityValues = (activitySets || []).map(({ activities }) => activities).flat();
        let graphMax = Math.max(10, ...activityValues);
        graphMax = Math.ceil(graphMax / tickGap) * tickGap;

        const chart = new Chart(document.getElementById('activities-canvas'), {
            type: 'line',
            data: {
                labels: currentDateRange.map(({ weekday, date }) => [weekday, date]),
                datasets: [
                    {
                        type: 'bar',
                        data: Array.from(Array(7)).fill(graphMax),
                        backgroundColor: 'transparent',
                        hoverBackgroundColor: theme.palette.ensightful.primary[50],
                        barThickness: 30,
                        order: 2,
                    },
                    ...(activitySets || []).map(({ activities, backgroundColor, label }) => ({
                        label,
                        data: activities,
                        backgroundColor,
                        hoverBackgroundColor: backgroundColor,
                        borderWidth: 2,
                        borderColor: backgroundColor,
                        pointBorderColor: theme.palette.ensightful.shades[0],
                        pointHoverBorderColor: theme.palette.ensightful.shades[0],
                        pointBorderWidth: 2,
                        pointHoverBorderWidth: 2,
                        pointRadius: 5,
                        pointHoverRadius: 5,
                        order: 1,
                    })),
                ],
            },
            options: {
                animation: { duration: 0 },
                interaction: { mode: 'index' },
                plugins: {
                    legend: { display: false },
                    tooltip: { enabled: false, external: (context) => renderExternalTooltip(context, theme) },
                },
                scales: {
                    x: {
                        grid: { display: false, drawBorder: false },
                        ticks: { font: { family: 'Sofia Pro', size: 14, weight: 500 } },
                    },
                    y: {
                        beginAtZero: true,
                        grid: { drawBorder: false },
                        suggestedMin: 10,
                        ticks: {
                            callback: (value, _, values) => {
                                setTickGap(values[1].value - values[0].value);
                                return value;
                            },
                        },
                    },
                },
            },
        });

        return () => {
            chart.destroy();
        };
    }, [activitySets, currentDateRange, tickGap, renderExternalTooltip]);

    const PageInner = (
        <Stack spacing={4}>
            <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                <Text variant={'sh2'} weight={'semibold'}>
                    {getGraphTitle(state.type)}
                </Text>
                <IconButton
                    aria-label={'show filters'}
                    kind={'filter'}
                    size={'medium'}
                    variant={'square'}
                    onClick={() => setOpen(true)}
                />
            </Stack>
            <Text color={'light'} variant={'p2'}>
                {getGraphSubtitle(state.type)}
            </Text>
            <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                <IconButton
                    kind={'chevronLeft'}
                    size={'medium'}
                    tooltip={{ component: 'Previous week' }}
                    variant={'square'}
                    onClick={() =>
                        setCurrentDateRange((current) => getDateRange(current[6].moment.subtract(7, 'days')))
                    }
                />
                <Text variant={'p1'} weight={'semibold'}>
                    {currentDateRange[0].moment.format('MMMM D')} - {currentDateRange[6].moment.format('MMMM D')}
                </Text>
                <IconButton
                    disabled={currentDateRange[6].moment.format('MMMM D YYYY') === moment().format('MMMM D YYYY')}
                    kind={'chevronRight'}
                    size={'medium'}
                    tooltip={{ component: 'Next week' }}
                    variant={'square'}
                    onClick={() => setCurrentDateRange((current) => getDateRange(current[6].moment.add(7, 'days')))}
                />
            </Stack>
            <CustomBox>
                <canvas id={'activities-canvas'} />
                {!activitySets && <CustomLoadingPage />}
            </CustomBox>
            <Stack spacing={4}>
                {state.display.map((item, index) => {
                    const legendItem = getLegendItem(item, index);
                    return (
                        <Stack key={item} alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                            <Text variant={'p2'}>{legendItem.text}</Text>
                            <Tooltip title={legendItem.text}>
                                <LegendColorBox
                                    aria-description={legendItem.text}
                                    aria-label={undefined}
                                    color={legendItem.color}
                                    role={'presentation'}
                                />
                            </Tooltip>
                        </Stack>
                    );
                })}
            </Stack>
        </Stack>
    );

    return (
        <>
            {auth.role === 'instructor' ? PageInner : <Container device={'mobile'}>{PageInner}</Container>}
            <Drawer device={'mobile'} open={open} onClose={() => setOpen(false)}>
                <Stack>
                    <IconButton
                        aria-label={'close'}
                        sx={{ alignSelf: 'flex-end' }}
                        kind={'exit'}
                        size={'medium'}
                        variant={'square'}
                        onClick={() => setOpen(false)}
                    />
                    <Stack spacing={10}>
                        <Stack spacing={2}>
                            <Text variant={'sh2'} weight={'semibold'}>
                                Graph Type
                            </Text>
                            <CustomRadioGroup
                                value={state.type}
                                onChange={(_, value) => setState((current) => ({ ...current, type: value }))}
                            >
                                <Radio label={'Log-In Activity'} labelPlacement={'start'} value={'login'} />
                                <Radio label={'Tasks Created'} labelPlacement={'start'} value={'tasks-created'} />
                                <Radio label={'Task Updates'} labelPlacement={'start'} value={'tasks-updates'} />
                                <Radio label={'Tasks Completed'} labelPlacement={'start'} value={'tasks-completed'} />
                            </CustomRadioGroup>
                        </Stack>
                        <Stack spacing={2}>
                            <Text variant={'sh2'} weight={'semibold'}>
                                Display
                            </Text>
                            <CheckboxStack>
                                <Checkbox
                                    checked={state.display.includes('team')}
                                    label={'Team Average'}
                                    labelPlacement={'start'}
                                    value={'team'}
                                    onChange={handleChange}
                                />
                                <Checkbox
                                    checked={state.display.includes('class')}
                                    label={'Class Average'}
                                    labelPlacement={'start'}
                                    value={'class'}
                                    onChange={handleChange}
                                />
                                {currentGroupAccountUsers.map((accountUser) => (
                                    <Checkbox
                                        key={accountUser.owner_account_id}
                                        checked={state.display.includes(accountUser.owner_account_id)}
                                        label={getUserPreferredFullName(accountUser)}
                                        labelPlacement={'start'}
                                        value={accountUser.owner_account_id}
                                        onChange={handleChange}
                                    />
                                ))}
                            </CheckboxStack>
                        </Stack>
                    </Stack>
                </Stack>
            </Drawer>
        </>
    );
};

const CustomBox = styled(Box)(() => {
    return {
        position: 'relative',
        width: '100%',
    };
});

const CustomLoadingPage = styled(LoadingPage)(() => {
    return {
        position: 'absolute',
        top: '50%',
        left: '50%',
        width: '100%',
        height: '100%',
        paddingBottom: '10%',
        background: 'rgba(255, 255, 255, 0.5)',
        transform: 'translate(-50%, -50%)',
    };
});

const LegendColorBox = styled(Box)(({ color }) => {
    return {
        borderRadius: '4px',
        width: 100,
        height: 6,
        background: color,
    };
});

const CustomRadioGroup = styled(RadioGroup)(() => {
    return {
        '& label': {
            justifyContent: 'space-between',
        },
    };
});

const CheckboxStack = styled(Stack)(() => {
    return {
        '& label': {
            justifyContent: 'space-between',
        },
    };
});

export default Mobile;
